import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LanguageSwitcher } from '../components';
import './TeacherDashboard.css';

type TabType = 'content' | 'schools' | 'settings';

interface School {
  id: string;
  teacher_id: string;
  name: string;
  name_fa?: string;
  description?: string;
  description_fa?: string;
  category?: string;
  status: string;
  created_at: string;
  updated_at: string;
  subscriber_count: number;
  content_count: number;
}

export function TeacherDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, isLoading, accessToken } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('content');

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isRTL = i18n.language === 'fa';

  // Handle redirects in useEffect
  useEffect(() => {
    if (!isLoading) {
      if (!user) {
        navigate('/login');
      } else if (user.role !== 'teacher' && user.role !== 'admin') {
        navigate('/dashboard');
      }
    }
  }, [user, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className={`teacher-dashboard ${isRTL ? 'rtl' : 'ltr'}`}>
        <div className="dashboard-loading">
          {t('common.loading')}
        </div>
      </div>
    );
  }

  // Show loading while redirecting
  if (!user || (user.role !== 'teacher' && user.role !== 'admin')) {
    return (
      <div className={`teacher-dashboard ${isRTL ? 'rtl' : 'ltr'}`}>
        <div className="dashboard-loading">
          {t('common.loading')}
        </div>
      </div>
    );
  }

  const openSCGS = () => {
    // SCGS Web UI URL - configurable via environment
    const scgsUrl = import.meta.env.VITE_SCGS_URL || 'http://localhost:3000';
    window.open(scgsUrl, '_blank');
  };

  return (
    <div className={`teacher-dashboard ${isRTL ? 'rtl' : 'ltr'}`}>
      {/* Header */}
      <header className="td-header">
        <div className="td-header-content">
          <a href="/" className="td-logo">
            <img src="/logo-header.png" alt="SolarMaktab" />
          </a>
          <div className="td-header-actions">
            <LanguageSwitcher />
            <button onClick={handleLogout} className="td-logout-btn">
              {t('common.logout')}
            </button>
          </div>
        </div>
      </header>

      <div className="td-layout">
        {/* Sidebar Navigation */}
        <aside className="td-sidebar">
          <div className="td-user-info">
            <div className="td-user-avatar">
              {user.name ? user.name.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
            </div>
            <div className="td-user-details">
              <span className="td-user-name">{user.name || user.email}</span>
              <span className="td-user-role">{t('auth.teacher')}</span>
            </div>
          </div>

          <nav className="td-nav">
            <button
              className={`td-nav-item ${activeTab === 'content' ? 'active' : ''}`}
              onClick={() => setActiveTab('content')}
            >
              <span className="td-nav-icon">📚</span>
              <span className="td-nav-label">{t('teacher.contentLibrary')}</span>
            </button>
            <button
              className={`td-nav-item ${activeTab === 'schools' ? 'active' : ''}`}
              onClick={() => setActiveTab('schools')}
            >
              <span className="td-nav-icon">🏫</span>
              <span className="td-nav-label">{t('schools.mySchools')}</span>
            </button>
            <button
              className={`td-nav-item ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              <span className="td-nav-icon">⚙️</span>
              <span className="td-nav-label">{t('teacher.settings')}</span>
            </button>
          </nav>

          <div className="td-sidebar-footer">
            <button className="td-create-btn" onClick={openSCGS}>
              <span className="td-create-icon">➕</span>
              <span>{t('teacher.createContent')}</span>
            </button>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="td-main">
          {activeTab === 'content' && <ContentLibraryTab />}
          {activeTab === 'schools' && <SchoolsTab accessToken={accessToken} />}
          {activeTab === 'settings' && <SettingsTab />}
        </main>
      </div>
    </div>
  );
}

// Content Library Tab Component
function ContentLibraryTab() {
  const { i18n } = useTranslation();
  
  const openSCGS = () => {
    const scgsUrl = import.meta.env.VITE_SCGS_URL || 'http://localhost:3000';
    window.open(scgsUrl, '_blank');
  };

  return (
    <div className="td-tab-content">
      <div className="td-tab-header">
        <h2>{i18n.language === 'fa' ? 'کتابخانه محتوا' : 'Content Library'}</h2>
        <button className="td-action-btn primary" onClick={openSCGS}>
          <span>➕</span>
          {i18n.language === 'fa' ? 'ایجاد محتوای جدید' : 'Create New Content'}
        </button>
      </div>

      <div className="td-empty-state">
        <div className="td-empty-icon">📚</div>
        <h3>{i18n.language === 'fa' ? 'هنوز محتوایی ندارید' : 'No content yet'}</h3>
        <p>
          {i18n.language === 'fa'
            ? 'برای ایجاد اولین درس خود، روی دکمه "ایجاد محتوای جدید" کلیک کنید.'
            : 'Click "Create New Content" to create your first lesson.'}
        </p>
        <button className="td-action-btn primary" onClick={openSCGS}>
          {i18n.language === 'fa' ? 'شروع ایجاد محتوا' : 'Start Creating Content'}
        </button>
      </div>
    </div>
  );
}

// Schools Tab Component
function SchoolsTab({ accessToken }: { accessToken: string | null }) {
  const { t, i18n } = useTranslation();
  const [schools, setSchools] = useState<School[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSchools = useCallback(async () => {
    if (!accessToken) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/schools', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Failed to fetch schools');
      }

      setSchools(data.data?.schools || []);
    } catch (err) {
      console.error('Error fetching schools:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch schools');
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchSchools();
  }, [fetchSchools]);

  const handleSchoolCreated = (newSchool: School) => {
    setSchools(prev => [newSchool, ...prev]);
    setShowCreateForm(false);
  };

  const handleSchoolDeleted = (schoolId: string) => {
    setSchools(prev => prev.filter(s => s.id !== schoolId));
  };

  if (isLoading) {
    return (
      <div className="td-tab-content">
        <div className="td-loading">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="td-tab-content">
      <div className="td-tab-header">
        <h2>{i18n.language === 'fa' ? 'مدارس مجازی من' : 'My Virtual Schools'}</h2>
        <button 
          className="td-action-btn primary"
          onClick={() => setShowCreateForm(true)}
        >
          <span>➕</span>
          {i18n.language === 'fa' ? 'ایجاد مدرسه جدید' : 'Create New School'}
        </button>
      </div>

      {error && (
        <div className="td-error-message">
          {error}
        </div>
      )}

      {showCreateForm && (
        <CreateSchoolForm
          accessToken={accessToken}
          onClose={() => setShowCreateForm(false)}
          onCreated={handleSchoolCreated}
        />
      )}

      {schools.length === 0 && !showCreateForm ? (
        <div className="td-empty-state">
          <div className="td-empty-icon">🏫</div>
          <h3>{i18n.language === 'fa' ? 'هنوز مدرسه‌ای ندارید' : 'No schools yet'}</h3>
          <p>
            {i18n.language === 'fa'
              ? 'یک مدرسه مجازی ایجاد کنید تا دانش‌آموزان بتوانند به محتوای شما دسترسی داشته باشند.'
              : 'Create a virtual school so students can subscribe to your content.'}
          </p>
          <button 
            className="td-action-btn primary"
            onClick={() => setShowCreateForm(true)}
          >
            {i18n.language === 'fa' ? 'ایجاد اولین مدرسه' : 'Create Your First School'}
          </button>
        </div>
      ) : (
        <div className="td-schools-grid">
          {schools.map(school => (
            <SchoolCard 
              key={school.id} 
              school={school} 
              accessToken={accessToken}
              onDeleted={handleSchoolDeleted}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Create School Form Component
function CreateSchoolForm({ 
  accessToken, 
  onClose, 
  onCreated 
}: { 
  accessToken: string | null;
  onClose: () => void;
  onCreated: (school: School) => void;
}) {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    name_fa: '',
    description: '',
    description_fa: '',
    category: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const categories = [
    { value: 'math', label: t('schools.categories.math') },
    { value: 'science', label: t('schools.categories.science') },
    { value: 'language', label: t('schools.categories.language') },
    { value: 'history', label: t('schools.categories.history') },
    { value: 'arts', label: t('schools.categories.arts') },
    { value: 'technology', label: t('schools.categories.technology') },
    { value: 'other', label: t('schools.categories.other') }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError(i18n.language === 'fa' ? 'نام مدرسه الزامی است' : 'School name is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/api/schools', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          name_fa: formData.name_fa.trim() || undefined,
          description: formData.description.trim() || undefined,
          description_fa: formData.description_fa.trim() || undefined,
          category: formData.category || undefined
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Failed to create school');
      }

      onCreated(data.data.school);
    } catch (err) {
      console.error('Error creating school:', err);
      setError(err instanceof Error ? err.message : 'Failed to create school');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="td-form-card">
      <div className="td-form-header">
        <h3>{i18n.language === 'fa' ? 'ایجاد مدرسه جدید' : 'Create New School'}</h3>
        <button className="td-close-btn" onClick={onClose}>×</button>
      </div>

      {error && (
        <div className="td-form-error">{error}</div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="td-form-row">
          <div className="td-form-group">
            <label>{t('schools.schoolName')} *</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder={i18n.language === 'fa' ? 'نام مدرسه به انگلیسی' : 'School name'}
              required
            />
          </div>
          <div className="td-form-group">
            <label>{t('schools.schoolNameFa')}</label>
            <input
              type="text"
              value={formData.name_fa}
              onChange={e => setFormData(prev => ({ ...prev, name_fa: e.target.value }))}
              placeholder={i18n.language === 'fa' ? 'نام مدرسه به فارسی' : 'School name in Persian'}
              dir="rtl"
            />
          </div>
        </div>

        <div className="td-form-group">
          <label>{t('schools.category')}</label>
          <select
            value={formData.category}
            onChange={e => setFormData(prev => ({ ...prev, category: e.target.value }))}
          >
            <option value="">{t('schools.selectCategory')}</option>
            {categories.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>

        <div className="td-form-row">
          <div className="td-form-group">
            <label>{t('schools.schoolDescription')}</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder={i18n.language === 'fa' ? 'توضیحات به انگلیسی' : 'Description'}
              rows={3}
            />
          </div>
          <div className="td-form-group">
            <label>{t('schools.schoolDescriptionFa')}</label>
            <textarea
              value={formData.description_fa}
              onChange={e => setFormData(prev => ({ ...prev, description_fa: e.target.value }))}
              placeholder={i18n.language === 'fa' ? 'توضیحات به فارسی' : 'Description in Persian'}
              rows={3}
              dir="rtl"
            />
          </div>
        </div>

        <div className="td-form-actions">
          <button type="button" className="td-action-btn secondary" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="td-action-btn primary" disabled={isSubmitting}>
            {isSubmitting 
              ? (i18n.language === 'fa' ? 'در حال ایجاد...' : 'Creating...') 
              : t('schools.createSchool')}
          </button>
        </div>
      </form>
    </div>
  );
}

// School Card Component
function SchoolCard({ 
  school, 
  accessToken,
  onDeleted 
}: { 
  school: School;
  accessToken: string | null;
  onDeleted: (id: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);

  const displayName = i18n.language === 'fa' && school.name_fa ? school.name_fa : school.name;
  const displayDescription = i18n.language === 'fa' && school.description_fa 
    ? school.description_fa 
    : school.description;

  const categoryLabel = school.category 
    ? t(`schools.categories.${school.category}`) 
    : null;

  const handleDelete = async () => {
    if (!confirm(i18n.language === 'fa' 
      ? 'آیا مطمئن هستید که می‌خواهید این مدرسه را حذف کنید؟' 
      : 'Are you sure you want to delete this school?')) {
      return;
    }

    setIsDeleting(true);

    try {
      const response = await fetch(`/api/schools/${school.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error?.message || 'Failed to delete school');
      }

      onDeleted(school.id);
    } catch (err) {
      console.error('Error deleting school:', err);
      alert(err instanceof Error ? err.message : 'Failed to delete school');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="td-school-card">
      <div className="td-school-header">
        <h3>{displayName}</h3>
        {categoryLabel && (
          <span className="td-school-category">{categoryLabel}</span>
        )}
      </div>
      
      {displayDescription && (
        <p className="td-school-description">{displayDescription}</p>
      )}

      <div className="td-school-stats">
        <div className="td-stat">
          <span className="td-stat-value">{school.subscriber_count}</span>
          <span className="td-stat-label">{t('schools.subscribers')}</span>
        </div>
        <div className="td-stat">
          <span className="td-stat-value">{school.content_count}</span>
          <span className="td-stat-label">{t('schools.contents')}</span>
        </div>
      </div>

      <div className="td-school-actions">
        <button className="td-action-btn secondary small" disabled>
          {t('common.edit')}
        </button>
        <button 
          className="td-action-btn danger small" 
          onClick={handleDelete}
          disabled={isDeleting}
        >
          {isDeleting 
            ? (i18n.language === 'fa' ? 'در حال حذف...' : 'Deleting...') 
            : t('common.delete')}
        </button>
      </div>
    </div>
  );
}

// Settings Tab Component
function SettingsTab() {
  const { i18n } = useTranslation();
  const { user } = useAuth();

  return (
    <div className="td-tab-content">
      <div className="td-tab-header">
        <h2>{i18n.language === 'fa' ? 'تنظیمات' : 'Settings'}</h2>
      </div>

      <div className="td-settings-section">
        <h3>{i18n.language === 'fa' ? 'اطلاعات حساب' : 'Account Information'}</h3>
        <div className="td-settings-card">
          <div className="td-settings-row">
            <span className="td-settings-label">{i18n.language === 'fa' ? 'ایمیل' : 'Email'}</span>
            <span className="td-settings-value">{user?.email}</span>
          </div>
          <div className="td-settings-row">
            <span className="td-settings-label">{i18n.language === 'fa' ? 'نام' : 'Name'}</span>
            <span className="td-settings-value">{user?.name || '-'}</span>
          </div>
          <div className="td-settings-row">
            <span className="td-settings-label">{i18n.language === 'fa' ? 'نقش' : 'Role'}</span>
            <span className="td-settings-value">{i18n.language === 'fa' ? 'معلم' : 'Teacher'}</span>
          </div>
        </div>
      </div>

      <div className="td-settings-section">
        <h3>{i18n.language === 'fa' ? 'تنظیمات SCGS' : 'SCGS Settings'}</h3>
        <div className="td-settings-card">
          <p className="td-settings-description">
            {i18n.language === 'fa'
              ? 'برای ایجاد محتوای جدید، از ابزار SCGS استفاده کنید.'
              : 'Use the SCGS tool to create new content.'}
          </p>
          <button 
            className="td-action-btn secondary"
            onClick={() => {
              const scgsUrl = import.meta.env.VITE_SCGS_URL || 'http://localhost:3000';
              window.open(scgsUrl, '_blank');
            }}
          >
            {i18n.language === 'fa' ? 'باز کردن SCGS' : 'Open SCGS'}
          </button>
        </div>
      </div>
    </div>
  );
}
